import { graphHooks } from '@tunasong/graph-lib/react'
import { canWriteEntity, isBandPinned } from '@tunasong/models'
import { useCurrentUser } from '@tunasong/redux'
import { isBandSpace } from '@tunasong/schemas'
import type { BandSpace, Entity, Persisted } from '@tunasong/schemas'
import { useParentChain } from '../navigation/parent-chain.js'

/**
 * The band space an item belongs to (GH #2822): the nearest band space above it. An item belongs to at most one
 * band, so there is no picker; the band itself is never its own pin target.
 */
export const nearestBandSpace = (
  chain: readonly Persisted<Entity>[],
  entityId: string
): Persisted<BandSpace> | undefined =>
  [...chain].reverse().find((candidate): candidate is Persisted<BandSpace> => {
    if (candidate.id === entityId) {
      return false
    }
    return isBandSpace(candidate)
  })

/** The item's band, whether the current user can change its pins, and whether the item is pinned to it */
export const useBandPin = (entity?: Persisted<Entity>) => {
  const chain = useParentChain(entity)
  const { userId } = useCurrentUser()
  const ancestor = entity ? nearestBandSpace(chain, entity.id) : undefined
  /** The ancestor chain can lag a pin made a moment ago; the band entity itself is current */
  const { entity: current } = graphHooks.useEntity<BandSpace>(ancestor?.id)
  const band = current ?? ancestor
  const canEdit = Boolean(band && canWriteEntity(userId, band))
  const pinned = Boolean(band && entity && isBandPinned(band.pins, entity.id))
  return { band, canEdit, pinned }
}
